/**
 * This component contains code to add a user in service portal, only admin can add a new user.
 * Admin can activate and deactivate to any other users but not to himself.
 */
import React from "react";
import Button from "@mui/material/Button";
import {
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Stack,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchSharp";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import useForm from "hooks/useForm";
import "./AddUser.scss";
import ADUser from "types/ADUser.type";
import RolesDropdown from "components/RolesDropdown";
import { useQueryClient } from "@tanstack/react-query";
import { useUserSearch, useAddNewUser } from "services/users.service";
import DashedDivider from "common/ui/DashedDivider";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { selectAddUser } from "context/modal.selector";
import { setAddUser } from "context/modal.context";
import toast from "react-hot-toast";
import LoadingButton from "common/ui/LoadingButton";

function AddUser() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectAddUser);

  // component states
  const [searchText, setSearchText] = React.useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = React.useState<ADUser | null>(null);
  const searchInput = React.useRef(null);
  const showRolesMenu = Boolean(anchorEl);

  // form state
  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: { userRoles: "" }
  });

  // server state
  const searchSuggestions = useUserSearch(searchText, onSearchSuccess);

  const addNewUser = useAddNewUser(onUserAdded);

  async function handleSearch(event) {
    event.preventDefault();

    const text = event.target.elements.search.value;

    if (!Boolean(text)) {
      setSearchText("");
      setSelectedUser(null);
    } else if (searchText === text) {
      if (searchSuggestions.isError) {
        await searchSuggestions.refetch();
      } else {
        onSearchSuccess();
      }
    } else if (text.length >= 3) {
      setSearchText(text);
    }
  }

  function onSearchSuccess() {
    setAnchorEl(searchInput.current);
  }

  function handleCloseRolesMenu() {
    setAnchorEl(null);
  }

  function selectUser(suggestion: ADUser) {
    return () => {
      setAnchorEl(null);
      if (props.users.some((user) => user.email === suggestion.mail)) {
        toast(t("addUser.userAlreadyAvailable"), {
          icon: "⚠️"
        });
        setSelectedUser(null);
      } else {
        setSelectedUser(suggestion);
      }
    };
  }

  function dismiss() {
    dispatch(setAddUser({ open: false, users: [], roles: [] }));
  }

  function handleAddNewUser(data) {
    const [firstName, ...lastName] = selectedUser.displayName.split(" ");
    const roleIds = data.userRoles.map((role) => {
      const [roleId] = role.split("|");
      return { roleId: roleId };
    });

    addNewUser.mutate({
      userName: selectedUser.mail,
      firstName,
      lastName: lastName.join(" "),
      email: selectedUser.mail,
      refId: selectedUser.id,
      isActive: true,
      roles: roleIds
    });
  }

  function onUserAdded(_data, error) {
    queryClient.invalidateQueries({ queryKey: ["all-portal-users"] });

    let message = t("addUser.userAddedSuccessfully");

    if (!error) {
      toast.success(message);
    }

    // clear state before confirm
    setSelectedUser(null);
    setSearchText("");
    clearErrors();
    dismiss();
  }

  function onModalDismiss() {
    // clear state before dismiss
    setSelectedUser(null);
    setSearchText("");
    clearErrors();
    dismiss();
  }

  return (
    <Dialog open={props.open} maxWidth="md" fullWidth>
      <DialogTitle
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {t("addUser.header")}
        <Stack direction="row" spacing={2}>
          <form onSubmit={handleSearch}>
            <TextField
              id="search"
              type="search"
              name="search"
              inputRef={searchInput}
              size="small"
              placeholder={t("addUser.searchUser")}
              sx={{ width: 350 }}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit">
                    <SearchIcon />
                  </IconButton>
                )
              }}
            />
          </form>
          {searchSuggestions.isSuccess ? (
            <Menu
              anchorEl={anchorEl}
              open={showRolesMenu}
              onClose={handleCloseRolesMenu}
            >
              {searchSuggestions.data.map((suggestion) => (
                <MenuItem
                  onClick={selectUser(suggestion)}
                  key={suggestion.id}
                  sx={{ width: 350 }}
                >
                  <ListItemText
                    primary={suggestion.displayName}
                    primaryTypographyProps={{
                      color: "primary.dark",
                      fontWeight: "medium"
                    }}
                    secondary={suggestion.mail}
                    secondaryTypographyProps={{
                      color: "primary.dark",
                      fontWeight: "light"
                    }}
                  />
                  <ListItemIcon>
                    <AddIcon color="primary" />
                  </ListItemIcon>
                </MenuItem>
              ))}
              {searchSuggestions.data.length === 0 && (
                <MenuItem onClick={handleCloseRolesMenu} sx={{ width: 350 }}>
                  <ListItemText primary={t("addUser.noUsersFound")} />
                </MenuItem>
              )}
            </Menu>
          ) : null}
          <IconButton onClick={onModalDismiss}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DashedDivider style={{ width: "95%" }} />
      <DialogContent classes={{ root: "add-user-modal__content" }}>
        {selectedUser ? (
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={5}>
              <Typography noWrap fontWeight="medium" color="primary.dark">
                {selectedUser.displayName}
              </Typography>
              <Typography noWrap fontWeight="light" color="primary.dark">
                {selectedUser.mail}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <RolesDropdown
                roles={props.roles}
                inputProps={{
                  ...register("userRoles", {
                    required: t("addUser.roleRequired")
                  })
                }}
                error={errors?.userRoles}
              />
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end">
              <LoadingButton
                variant="outlined"
                onClick={handleSubmit(handleAddNewUser)}
                loading={addNewUser.isLoading}
              >
                {t("addUser.addUser")}
              </LoadingButton>
            </Grid>
          </Grid>
        ) : (
          <DialogContentText variant="h5">
            {t("addUser.emptySearchText")}
          </DialogContentText>
        )}
      </DialogContent>
      <DashedDivider style={{ width: "95%" }} />
      <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
        <Button
          size="large"
          variant="outlined"
          color="gray"
          onClick={onModalDismiss}
        >
          {t("common.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddUser;
